import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getPeitepaksuusV2, updatePeitepaksuusV2,
  createPeitepaksuusArvoV2, updatePeitepaksuusArvoV2, deletePeitepaksuusArvoV2, replacePeitepaksuusArvotV2,
} from '../../../../../api/api';
import useApiFeedback from './useApiFeedback';
import { parseCaptionForEditor, serializeCaptionForSave } from './captionUtils';

// Service layer for PeitepaksuusView. All data + API calls live here; the
// view only renders props. karb side is READ-ONLY here — those values are
// derived from poranäyte server-side, never edited in this block (also
// enforced by the backend: PATCH peitepaksuus 422s on any karb-ish field).
//
// coverRows is [{ id, value, _localKey }] — id is the row's real
// peitepaksuus_arvo id (null for the not-yet-saved trailing slot), and
// _localKey is a PERMANENT client-side identity assigned once per row and
// never regenerated, even after `id` transitions from null to real, or
// after any refresh. This is what actually makes the input stable: Poränäyte
// never has this problem because every one of its rows always has a real DB
// id from the moment it's created (via an explicit "Lisää näyte" action) —
// Peitepaksuus's cover-measurement table has a v1-style "always show a
// growing empty row" slot that has no id yet, so it needs its own stable
// identity to key React's reconciliation by (see PeitepaksuusView.jsx's
// MeasurementTable, which uses uncontrolled defaultValue inputs keyed by
// _localKey — exactly like Poränäyte's `key={s.id}` + `defaultValue`).
//
// Saving a row PATCHes/POSTs/DELETEs just that one row instead of replacing
// the whole array on every edit — two rows saved close together used to
// each fire their own "destroy everything, recreate everything" request,
// and whichever wrote last on the server silently won, erasing whatever the
// other, newer edit had just added. Per-row writes can't race like that.
let localKeySeq = 0;
const newLocalKey = () => `new_${++localKeySeq}`;

export default function usePeitepaksuusV2(rakId) {
  const [peite, setPeite] = useState(null);
  const [coverRows, setCoverRows] = useState([{ id: null, value: '', _localKey: newLocalKey() }]);
  const [ageInput, setAgeInput] = useState('');
  const [error, setError] = useState(''); // load-only; mutation results use feedback
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { feedback, run, clearFeedback } = useApiFeedback();

  // Counts in-flight cover-row saves so the view can show a "Tallennetaan…"
  // indicator — several rows can be saving at once (rapid typing, or a
  // paste), so this is a count, not a flag, and only goes back to false
  // once every one of them has actually finished.
  const pendingSavesRef = useRef(0);
  const beginSave = useCallback(() => { pendingSavesRef.current += 1; setSaving(true); }, []);
  const endSave = useCallback(() => {
    pendingSavesRef.current = Math.max(0, pendingSavesRef.current - 1);
    if (pendingSavesRef.current === 0) setSaving(false);
  }, []);

  // Discards a refresh's response if a newer refresh has since been kicked
  // off — otherwise an older (but later-arriving) response could briefly
  // clobber the UI with stale data after a more recent edit already landed.
  const refreshSeqRef = useRef(0);

  const refresh = useCallback(() => {
    if (!rakId) { setPeite(null); return; }
    const seq = ++refreshSeqRef.current;
    setLoading(true);
    getPeitepaksuusV2(rakId)
      .then(r => {
        if (seq !== refreshSeqRef.current) return;
        setPeite(r.data);
        setAgeInput(String(r.data.age ?? ''));
        setCoverRows(prev => {
          // Saved rows always get a deterministic key from their id, so
          // it's identical (and the input stays mounted) across repeated
          // refreshes. Any row the user hasn't saved yet (id still null —
          // they're mid-typing, or a create is in flight) is spliced back
          // in completely untouched: same object, same _localKey, same
          // uncontrolled input, same whatever they've typed.
          const pending = prev.filter(row => row.id == null);
          const saved = r.data.cover_measurements.map(m => ({ id: m.id, value: String(m.arvo_mm), _localKey: `id_${m.id}` }));
          return pending.length ? [...saved, ...pending] : [...saved, { id: null, value: '', _localKey: newLocalKey() }];
        });
      })
      .catch(e => { if (seq === refreshSeqRef.current) setError(e?.response?.data?.error || e.message); })
      .finally(() => { if (seq === refreshSeqRef.current) setLoading(false); });
  }, [rakId]);
  useEffect(refresh, [refresh]);

  // These three (patch/create/remove) deliberately do NOT call refresh()
  // afterward — that's a big part of what made saving feel slow. A refresh
  // recomputes the full payload (up to 7 sequential DB round-trips: the
  // peite/poränäyte/arvo lookups, plus — when no manual_age is set —
  // _autoAgeForRakenneosa's 4-query chain to find the building's
  // construction year), and it ran after every single keystroke-commit even
  // though none of that derived data (stats/coeff/karbonatisaatio) is even
  // visible while editing (it's on the separate "Laskut ja kuvaajat" tab).
  // Each row's own value is already correct locally (uncontrolled input, or
  // the real id handed straight back by the create response), so there's
  // nothing here a refetch would actually improve — the 10s background
  // poll below keeps everything else eventually consistent.
  const patchCoverRow = useCallback(async (id, value) => {
    beginSave();
    try { await run(() => updatePeitepaksuusArvoV2(id, value)); }
    catch { /* captured in feedback */ }
    finally { endSave(); }
  }, [run, beginSave, endSave]);

  // Returns the created row (or null on failure) so the view can patch the
  // matching local row's `id` in place — by _localKey, not array index —
  // once the POST resolves.
  const createCoverRow = useCallback(async (value) => {
    if (!peite) return null;
    beginSave();
    let created = null;
    try { created = (await run(() => createPeitepaksuusArvoV2(peite.id, value))).data; }
    catch { /* captured in feedback */ }
    finally { endSave(); }
    return created;
  }, [peite, run, beginSave, endSave]);

  const removeCoverRow = useCallback(async (id) => {
    beginSave();
    try { await run(() => deletePeitepaksuusArvoV2(id)); }
    catch { /* captured in feedback */ }
    finally { endSave(); }
  }, [run, beginSave, endSave]);

  // Paste-many-values only — a single atomic replace-everything gesture,
  // not two competing per-row edits, so the bulk endpoint is fine here.
  const replaceCoverRows = useCallback(async (values) => {
    if (!peite) return;
    beginSave();
    try { await run(() => replacePeitepaksuusArvotV2(peite.id, values)); }
    catch { /* captured in feedback */ }
    finally { refresh(); endSave(); }
  }, [peite, run, refresh, beginSave, endSave]);

  const commitAge = useCallback(async (val) => {
    if (!peite) return;
    const num = parseFloat(val);
    try { await run(() => updatePeitepaksuusV2(peite.id, { manual_age: num > 0 ? num : null })); }
    catch { /* captured in feedback */ }
    finally { refresh(); }
  }, [peite, run, refresh]);

  // TaulukkoFooter's onChange fires on every keystroke (Lexical's
  // OnChangePlugin fires per edit, not on blur) — debounce the actual save
  // so typing doesn't PATCH per character, same 600ms pattern Poränäyte's
  // Lisähuomioita field uses. Also deliberately does NOT call refresh()
  // afterward: that recomputes the whole Peitepaksuus payload (cover
  // measurements, karb stats, age — up to 7 DB round-trips) for an edit
  // that only ever touches one text column, which is what made every
  // caption save flash the table's full loading spinner. The PATCH
  // response already confirms the saved value, so it's merged into peite
  // directly instead.
  const captionTimers = useRef({});
  const commitCaption = useCallback((capData) => {
    if (!peite) return;
    const targetId = peite.id; // guards against the user switching rakenneosa mid-debounce
    const serialized = serializeCaptionForSave(capData);
    clearTimeout(captionTimers.current.caption);
    captionTimers.current.caption = setTimeout(async () => {
      try {
        await run(() => updatePeitepaksuusV2(targetId, { caption: serialized }));
        setPeite(prev => prev && prev.id === targetId ? { ...prev, caption: serialized } : prev);
      } catch { /* captured in feedback */ }
    }, 600);
  }, [peite, run]);

  const commitChartCaption = useCallback((capData) => {
    if (!peite) return;
    const targetId = peite.id;
    const serialized = serializeCaptionForSave(capData);
    clearTimeout(captionTimers.current.chart_caption);
    captionTimers.current.chart_caption = setTimeout(async () => {
      try {
        await run(() => updatePeitepaksuusV2(targetId, { chart_caption: serialized }));
        setPeite(prev => prev && prev.id === targetId ? { ...prev, chart_caption: serialized } : prev);
      } catch { /* captured in feedback */ }
    }, 600);
  }, [peite, run]);

  // Background safety net: catches drift from another tab/session editing
  // the same rakenneosa (a row added/removed elsewhere) — harmless to run
  // regardless, since refresh() never disturbs an in-progress edit (see
  // above), only reconciles by id.
  useEffect(() => {
    if (!rakId) return;
    const interval = setInterval(refresh, 10000);
    return () => clearInterval(interval);
  }, [rakId, refresh]);

  return {
    peite, loading, saving, error, feedback, clearFeedback,
    coverRows, setCoverRows, patchCoverRow, createCoverRow, removeCoverRow, replaceCoverRows,
    ageInput, setAgeInput, commitAge,
    caption: parseCaptionForEditor(peite?.caption), commitCaption,
    chartCaption: parseCaptionForEditor(peite?.chart_caption), commitChartCaption,
  };
}
